<div class="submit-page">
  <div class="submit-container">
    <!-- Page header -->
    <div class="page-header">
      <h1 class="page-title">Submit URLs</h1>
      <p class="page-subtitle">Add URLs to be indexed. Each unique URL costs 1 credit.</p>
    </div>

    <!-- Step indicator -->
    <div class="step-indicator">
      <div class="step" [class.active]="currentStep === 1" [class.complete]="currentStep > 1">
        <div class="step-num">1</div>
        <span>Add URLs</span>
      </div>
      <div class="step-line" [class.complete]="currentStep > 1"></div>
      <div class="step" [class.active]="currentStep === 2" [class.complete]="currentStep > 2">
        <div class="step-num">2</div>
        <span>Configure & submit</span>
      </div>
    </div>

    <!-- Success state -->
    <div *ngIf="isSuccess" class="success-card">
      <div class="success-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h2>{{ successMessage }}</h2>
      <p>Track progress in real-time on the dashboard.</p>
      <div class="success-actions">
        <a routerLink="/dashboard" class="btn btn-primary">View dashboard</a>
        <a routerLink="/history" class="btn btn-secondary">See history</a>
        <button class="btn btn-ghost" (click)="resetWizard()">Submit more</button>
      </div>
    </div>

    <!-- Step 1: Add URLs -->
    <div *ngIf="!isSuccess && currentStep === 1" class="card submit-card">
      <form #step1Form="ngForm">
        <!-- Method tabs -->
        <div class="method-tabs">
          <button type="button" class="method-tab" [class.active]="submitMethod === 'text'" (click)="setSubmitMethod('text')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>
            Paste URLs
          </button>
          <button type="button" class="method-tab" [class.active]="submitMethod === 'file'" (click)="setSubmitMethod('file')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Upload file
          </button>
        </div>

        <!-- Text input -->
        <div *ngIf="submitMethod === 'text'" class="text-input-section">
          <label class="form-label">URLs (one per line)</label>
          <textarea
            class="url-textarea"
            rows="10"
            [(ngModel)]="urlsText"
            (ngModelChange)="onUrlsChange()"
            placeholder="https://example.com/page-1&#10;https://example.com/page-2&#10;https://example.com/page-3"
            required
            name="urlsText"
            #urlsTextInput="ngModel"
          ></textarea>
          <div class="form-error" *ngIf="urlsTextInput.invalid && (urlsTextInput.dirty || urlsTextInput.touched)">
            <span *ngIf="urlsTextInput.errors?.['required']">At least one URL is required</span>
          </div>
          <div class="input-stats" *ngIf="uniqueUrls.length > 0 || duplicateCount > 0">
            <div class="stat-chip stat-success">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              {{ uniqueUrls.length }} valid
            </div>
            <div class="stat-chip stat-warning" *ngIf="duplicateCount > 0">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ duplicateCount }} duplicate
            </div>
            <div class="stat-chip stat-info" *ngIf="tldStats.length > 0">
              <span *ngFor="let tld of tldStats" class="tld-chip">.{{ tld.tld }} <span class="mono">{{ tld.count }}</span></span>
            </div>
          </div>
        </div>

        <!-- File upload -->
        <div *ngIf="submitMethod === 'file'" class="file-upload-section">
          <div *ngIf="!selectedFile"
               class="dropzone"
               (dragover)="onDragOver($event)"
               (drop)="onDrop($event)">
            <input type="file" id="fileInput" accept=".csv,.txt" (change)="onFileSelected($event)" hidden />
            <label for="fileInput" class="dropzone-label">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <p>Drop a CSV or TXT file here</p>
              <span>or click to browse</span>
            </label>
          </div>
          <div *ngIf="selectedFile" class="file-info">
            <div class="file-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div class="file-details">
              <div class="file-name">{{ selectedFile.name }}</div>
              <div class="file-meta">{{ formatFileSize(selectedFile.size) }} · {{ fileUrlCount }} valid URLs</div>
            </div>
            <button type="button" class="btn btn-icon btn-ghost" (click)="removeFile()" title="Remove">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="form-error" *ngIf="submitMethod === 'file' && !selectedFile && step1Form.submitted">
            <span>Please select a file</span>
          </div>
        </div>

        <div class="step-footer">
          <div></div>
          <button type="submit" class="btn btn-primary" (click)="step1Form.valid && nextStep()" [disabled]="getUrlCount() === 0">
            Continue
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>
      </form>
    </div>

    <!-- Step 2: Configure -->
    <div *ngIf="!isSuccess && currentStep === 2" class="card submit-card">
      <form #step2Form="ngForm">
        <div class="summary-row">
          <div>
            <div class="summary-label">URLs to submit</div>
            <div class="summary-value mono">{{ getUrlCount() }}</div>
          </div>
          <div>
            <div class="summary-label">Credits required</div>
            <div class="summary-value mono">{{ getUrlCount() }}</div>
          </div>
          <div>
            <div class="summary-label">After submission</div>
            <div class="summary-value mono">{{ userCredits - getUrlCount() }}</div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="form-group">
          <div class="toggle-row">
            <div>
              <label class="form-label" style="margin-bottom: 4px;">Drip Mode</label>
              <p class="form-hint">Spread submissions over time for natural indexing patterns</p>
            </div>
            <label class="toggle">
              <input type="checkbox" [(ngModel)]="dripEnabled" name="dripEnabled">
              <span class="toggle-slider"></span>
            </label>
          </div>
          <div *ngIf="dripEnabled" class="drip-interval">
            <label class="form-label">Interval (minutes between submissions)</label>
            <input type="number" class="form-control" [(ngModel)]="dripInterval" name="dripInterval" min="1" #dripIntervalInput="ngModel" />
            <div class="form-error" *ngIf="dripIntervalInput.invalid && (dripIntervalInput.dirty || dripIntervalInput.touched)">
              <span *ngIf="dripIntervalInput.errors?.['min']">Interval must be at least 1 minute</span>
            </div>
          </div>
        </div>

        <div *ngIf="errorMessage" class="auth-error">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>{{ errorMessage }}</span>
        </div>

        <div class="step-footer">
          <button type="button" class="btn btn-secondary" (click)="prevStep()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Back
          </button>
          <button type="submit" class="btn btn-primary" (click)="step2Form.valid && submitLinks()" [disabled]="isSubmitting">
            <span class="spinner spinner-sm" *ngIf="isSubmitting"></span>
            <span>{{ isSubmitting ? 'Submitting...' : 'Submit ' + getUrlCount() + ' URL' + (getUrlCount() === 1 ? '' : 's') }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
